// Libraries
import _ from 'lodash';
import React, {useMemo} from 'react';

// Supermove
import {Icon, DataLoader, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useQuery,
  useResponsive,
  useNavigationDOM,
  useModal,
  ToastType,
  useToast,
  ResponsiveType,
} from '@supermove/hooks';
import {
  CalendarIntegrationModel,
  getCalendarIntegrationJobCategoryLabel,
  OrganizationModel,
  AccountGrantModel,
  UserModel,
} from '@supermove/models';
import {SupermoveNavigatorType} from '@supermove/navigation/src/NavigationTypes';
import {colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

// App
import Button from '@shared/design/components/Button';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import EmptyState from '@shared/design/components/EmptyState';
import Table from '@shared/design/components/Table';
import {
  ColumnDefinitionType,
  ActionType,
} from '@shared/design/components/Table/components/TableBuilder';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import useToggleCalendarIntegrationIsEnabledMutation from '@shared/modules/AccountCalendar/hooks/useToggleCalendarIntegrationIsEnabledMutation';
import CreateAccountGrantForm from '@shared/modules/AccountGrant/forms/CreateAccountGrantForm';
import ProjectStatus from '@shared/modules/Project/enums/ProjectStatus';
import UserRole from '@shared/modules/User/enums/UserRole';
import Switch from 'modules/App/components/Switch';
import DeleteCalendarIntegrationModal from 'modules/Organization/Settings/Communication/components/DeleteCalendarIntegrationModal';
import DuplicateCalendarIntegrationModal from 'modules/Organization/Settings/Communication/components/DuplicateCalendarIntegrationModal';
import useConnectAccountGrant from 'modules/Organization/Settings/Communication/hooks/useConnectAccountGrant';

const PageContainer = Styled.View`
  flex: 1;
  background-color: ${colors.gray.background};
`;

const TooltipContainer = Styled.View`
`;

const StatusContainer = Styled.View<{color: string}>`
  border-radius: 4px;
  background-color: ${({color}) => color};
  padding-vertical: 4px;
  padding-horizontal: 8px;
`;

const JobCategoriesContainer = Styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`;

const EmptyStateContainer = Styled.View`
  padding-horizontal: 16px;
  padding-vertical: 24px;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const CalendarIntegrationsContentHeaderContainer = Styled.View`
  padding-horizontal: 24px;
  padding-vertical: 16px;
  z-index: 1;
`;

const HeadingTextContainer = Styled.View`
  flex: 1;
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const CalendarIntegrationsContentContainer = Styled.View<{desktop: boolean}>`
  flex: 1;
  ${({desktop}) => desktop && 'padding-horizontal: 24px;'}
`;

interface ColumnDefinitionProps {
  refetch: () => void;
  navigator: SupermoveNavigatorType;
  viewer: UserModel;
  successToast: ToastType;
  toggle: (id: string, isEnabled: boolean) => void;
  submitting: boolean;
  responsive: ResponsiveType;
}

const getActions = (
  calendarIntegration: CalendarIntegrationModel,
  navigator: SupermoveNavigatorType,
  viewer: UserModel,
  refetch: () => void,
  successToast: ToastType,
): ActionType<CalendarIntegrationModel>[] => {
  return [
    {
      text: 'Edit integration',
      onPress: () => {
        navigator.push(
          `/settings/communication/calendar-integrations/${calendarIntegration.uuid}/edit`,
        );
      },
    },
    {
      text: 'Duplicate integration',
      onPress: ({handleOpen}: {handleOpen?: () => void}) => {
        handleOpen && handleOpen();
      },
      actionHook: {
        hook: useModal,
        hookArgument: {name: 'Duplicate Calendar Integration Modal'},
        renderComponent: ({isOpen, handleClose}) => {
          return (
            <DuplicateCalendarIntegrationModal
              key={String(isOpen)}
              isOpen={isOpen}
              handleClose={handleClose}
              calendarIntegration={calendarIntegration}
              onSuccess={() => {
                refetch();
                successToast.handleToast({
                  message: 'Calendar integration duplicated successfully.',
                });
              }}
            />
          );
        },
      },
    },
    {
      text: 'Delete',
      color: colors.red.warning,
      onPress: ({handleOpen}: {handleOpen?: () => void}) => {
        handleOpen && handleOpen();
      },
      actionHook: {
        hook: useModal,
        hookArgument: {name: 'Delete Calendar Integration Modal'},
        renderComponent: ({isOpen, handleClose}) => {
          return (
            <DeleteCalendarIntegrationModal
              key={String(isOpen)}
              isOpen={isOpen}
              handleClose={handleClose}
              calendarIntegration={calendarIntegration}
              onSuccess={() => {
                refetch();
                successToast.handleToast({
                  message: 'Calendar integration removed successfully.',
                });
              }}
            />
          );
        },
      },
    },
  ];
};

const JobCategoryCell = ({
  calendarIntegration,
}: {
  calendarIntegration: CalendarIntegrationModel;
}) => {
  const jobCategories = useMemo(() => {
    return calendarIntegration.jobCategories;
  }, [calendarIntegration]);

  return (
    <JobCategoriesContainer>
      {jobCategories.map((category) => {
        return (
          <React.Fragment key={category}>
            <StatusContainer color={colors.gray.border}>
              <Text.MicroLabel>{getCalendarIntegrationJobCategoryLabel(category)}</Text.MicroLabel>
            </StatusContainer>
            <Space width={4} />
          </React.Fragment>
        );
      })}
    </JobCategoriesContainer>
  );
};

const getColumnDefinitions = ({
  refetch,
  navigator,
  viewer,
  successToast,
  toggle,
  submitting,
  responsive,
}: ColumnDefinitionProps): ColumnDefinitionType<CalendarIntegrationModel>[] => {
  return [
    {
      flex: 0.1,
      tooltip: (calendarIntegration) =>
        `Integration ${calendarIntegration.isEnabled ? 'enabled' : 'disabled'}`,
      mobileOptions: {
        isInHeader: true,
      },
      cellComponent: (calendarIntegration) => {
        return (
          <Row>
            <Switch
              isOn={calendarIntegration.isEnabled}
              onChange={() => toggle(calendarIntegration.id, !calendarIntegration.isEnabled)}
              isLoading={submitting}
            />
            {responsive.mobile && (
              <React.Fragment>
                <Space width={4} />
                <Text.Label>{calendarIntegration.accountCalendar.calendarName}</Text.Label>
              </React.Fragment>
            )}
          </Row>
        );
      },
    },
    {
      flex: 1,
      headerLabel: 'Calendar Name',
      tooltip: (calendarIntegration) => calendarIntegration.accountCalendar.calendarName,
      cellText: (calendarIntegration) => calendarIntegration.accountCalendar.calendarName,
      secondary: {
        headerLabel: 'Account',
        tooltip: (calendarIntegration) =>
          calendarIntegration.accountCalendar.accountGrant.identifier,
        cellText: (calendarIntegration) =>
          calendarIntegration.accountCalendar.accountGrant.identifier,
      },
    },
    {
      flex: 1,
      headerLabel: 'Project Statuses',
      cellText: (calendarIntegration) =>
        calendarIntegration.projectStatuses.map(ProjectStatus.getStatusLabel).join(', '),
      secondary: {
        headerLabel: 'Job Categories',
        cellComponent: (calendarIntegration) => (
          <JobCategoryCell calendarIntegration={calendarIntegration} />
        ),
      },
    },
    {
      flex: 1,
      headerLabel: 'Invite Staff',
      emptyText: 'None',
      cellText: (calendarIntegration) =>
        calendarIntegration.inviteRoles.map((role) => UserRole.getDisplayRole(role)).join(', '),
    },
    {
      flex: 1,
      headerLabel: 'Last Updated',
      cellText: (calendarIntegration) =>
        Datetime.convertToDisplayDatetime(calendarIntegration.updatedAt),
      secondary: {
        cellText: (calendarIntegration) => calendarIntegration.updatedBy?.fullName,
      },
    },
    {
      flex: 0.1,
      actions: (calendarIntegration) =>
        getActions(calendarIntegration, navigator, viewer, refetch, successToast),
    },
  ];
};

const ConnectAccountInfo = ({
  organization,
  refetch,
  successToast,
}: {
  organization: OrganizationModel;
  refetch: () => void;
  successToast: ToastType;
}) => {
  const createAccountGrantForm = CreateAccountGrantForm.new({
    kind: 'ORGANIZATION',
    provider: 'NYLAS',
    organizationId: organization.id,
  });

  const {connectAccountGrant, submitting: connectingAccount} = useConnectAccountGrant({
    createAccountGrantForm,
    onSuccess: () => {
      refetch();
      successToast.handleToast({
        message: 'Company account connected successfully.',
      });
    },
    refetchQueries: ['OrganizationSettingsCalendarIntegrationsPage'],
    redirectPath: '/settings/communication/calendar-integrations',
  });

  return (
    <Text.Body style={{color: colors.gray.tertiary}}>
      <Text.Link onPress={() => connectAccountGrant()}>Connect a company account</Text.Link> to
      integrate a calendar.
    </Text.Body>
  );
};

const EmptyTable = ({
  organization,
  refetch,
  successToast,
}: {
  organization: OrganizationModel;
  refetch: () => void;
  successToast: ToastType;
}) => {
  const {activeAccountGrants} = organization;

  return (
    <EmptyStateContainer>
      {activeAccountGrants.length === 0 ? (
        <ConnectAccountInfo
          organization={organization}
          refetch={refetch}
          successToast={successToast}
        />
      ) : (
        <EmptyState message={'No calendar integration created yet.'} />
      )}
    </EmptyStateContainer>
  );
};

const CalendarIntegrationsContentHeader = ({
  activeAccountGrants,
  handleCreateCalendarIntegration,
}: {
  activeAccountGrants: AccountGrantModel[];
  handleCreateCalendarIntegration: () => void;
}) => {
  const responsive = useResponsive();

  return (
    <CalendarIntegrationsContentHeaderContainer>
      <Row style={{justifyContent: 'space-between'}}>
        <HeadingTextContainer>
          <Text.Heading2>Calendar Integrations</Text.Heading2>
          <Space height={8} />
          <Text.Body>Sync Supermove projects to your company and work calendars.</Text.Body>
        </HeadingTextContainer>
        <Space width={24} />
        <TextTooltip
          placement={'top'}
          text={
            activeAccountGrants.length === 0
              ? 'Connect a company account to create calendar integration.'
              : undefined
          }
        >
          <TooltipContainer style={{width: 'fit-content'}}>
            {responsive.desktop ? (
              <Button
                iconLeft={Icon.Plus}
                text={'Create Integration'}
                isDisabled={activeAccountGrants.length === 0}
                onPress={handleCreateCalendarIntegration}
                style={{marginBottom: 24, alignSelf: 'flex-start'}}
              />
            ) : (
              <React.Fragment>
                <TertiaryButton
                  iconLeft={Icon.Plus}
                  text={'Create'}
                  isDisabled={activeAccountGrants.length === 0}
                  onPress={handleCreateCalendarIntegration}
                  isResponsive
                />
              </React.Fragment>
            )}
          </TooltipContainer>
        </TextTooltip>
      </Row>
    </CalendarIntegrationsContentHeaderContainer>
  );
};

const OrganizationSettingsCalendarIntegrationsPage = () => {
  const {navigator} = useNavigationDOM();
  const responsive = useResponsive();
  const successToast = useToast({
    ToastComponent: SuccessToast,
  });

  const {loading, data, refetch} = useQuery<{viewer: UserModel}>(
    OrganizationSettingsCalendarIntegrationsPage.query,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const handleCreateCalendarIntegration = () => {
    navigator.push('/settings/communication/calendar-integrations/create');
  };

  const {form, submitting, handleSubmit} = useToggleCalendarIntegrationIsEnabledMutation({
    calendarIntegrationId: 0,
    isEnabled: false,
    onSuccess: (calendarIntegration) => {
      refetch();
    },
    onError: (errors) => {
      console.log(errors);
    },
  });

  const toggle = (id: string, isEnabled: boolean) => {
    handleSubmit({
      variables: {
        calendarIntegrationForm: {
          calendarIntegrationId: _.toNumber(id),
          isEnabled,
        },
      },
    });
  };

  return (
    <PageContainer>
      <DataLoader loading={loading} data={data} as={null}>
        {({loadedData}) => {
          const {activeAccountGrants} = loadedData.viewer.viewingOrganization;
          return (
            <React.Fragment>
              <CalendarIntegrationsContentHeader
                activeAccountGrants={activeAccountGrants}
                handleCreateCalendarIntegration={handleCreateCalendarIntegration}
              />
              <CalendarIntegrationsContentContainer desktop={responsive.desktop}>
                <Table
                  isLoading={loading}
                  columnDefinitions={getColumnDefinitions({
                    refetch,
                    navigator,
                    viewer: loadedData.viewer,
                    successToast,
                    toggle,
                    submitting,
                    responsive,
                  })}
                  items={loadedData.viewer.viewingOrganization.calendarIntegrations || []}
                  EmptyStateComponent={() => (
                    <EmptyTable
                      organization={loadedData.viewer.viewingOrganization}
                      refetch={refetch}
                      successToast={successToast}
                    />
                  )}
                />
              </CalendarIntegrationsContentContainer>
            </React.Fragment>
          );
        }}
      </DataLoader>
    </PageContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsCalendarIntegrationsPage.query = gql`
  ${DeleteCalendarIntegrationModal.fragment}
  ${DuplicateCalendarIntegrationModal.fragment}
  query OrganizationSettingsCalendarIntegrationsPage {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        activeAccountGrants {
          id
        }
        calendarIntegrations {
          id
          uuid
          jobCategories
          projectStatuses
          inviteRoles
          isEnabled
          updatedAt
          updatedBy {
            id
            fullName
          }
          accountCalendar {
            id
            accountGrant {
              id
              identifier
            }
            calendarName
            deletedAt
          }
          ...DuplicateCalendarIntegrationModal
          ...DeleteCalendarIntegrationModal
        }
      }
    }
  }
`;

export default OrganizationSettingsCalendarIntegrationsPage;
